/**
 * De vaste a11y-teksten van de Aurora-componenten, uit i18n — issue 294.
 *
 * Aurora doet zelf geen `useTranslation()` (zie `auroraLabels.tsx` in de
 * Aurora-map); deze laag doet het één keer voor de hele app en geeft het
 * resultaat via `AuroraLabelsProvider` door. Hij staat in `main.tsx`, direct
 * binnen de `I18nextProvider`. Haal je hem daar weg, dan valt elke `Modal`,
 * `Alert`, wachtwoordveld en `Combobox` stil terug op Engels — zonder foutmelding.
 *
 * Een nieuwe sleutel in `AuroraLabels` hoort hier ook. Vergeet je dat, dan valt de
 * typecheck om: de hook geeft het volledige `AuroraLabels`-type terug.
 */
import { useEffect, useReducer, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AuroraLabelsProvider, type AuroraLabels } from '@aurora';

export function useAuroraI18nLabels(): AuroraLabels {
  const { t, i18n } = useTranslation();

  // ⚠️ DIT ABONNEMENT IS GEEN DETAIL. Deze hook draait bij de root en rendert dus
  // vrijwel nooit opnieuw. De omgevings-teksten (ADR-0021) komen ná de eerste render
  // binnen via `addResourceBundle`, en dat seint i18next alleen als store-event —
  // niet als taalwissel, waar `useTranslation` wel zelf op reageert. Zonder dit bleef
  // een overschreven sleutel hier op de standaardtekst staan tot iemand van taal
  // wisselde.
  //
  // Expliciet op `i18n.store` en niet via de optie `bindI18nStore` van
  // `useTranslation`: react-i18next leest die optie wel per hook, maar typt hem
  // alleen als globale instelling — en globaal zou élke `useTranslation` in de app
  // opnieuw renderen bij elke bundel die binnenkomt.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const onAdded = () => rerender();
    i18n.store.on('added', onAdded);
    return () => i18n.store.off('added', onAdded);
  }, [i18n]);

  // Bewust géén `useMemo`, net als in `useDataTableLabels`: deze hook rendert alleen
  // bij een taalwissel of een nieuwe bundel, en precies dan kunnen de teksten anders
  // zijn. Het zijn vijf string-lookups.
  return {
    modalClose: t('aurora.modalClose'),
    alertDismiss: t('aurora.alertDismiss'),
    passwordShow: t('aurora.passwordShow'),
    passwordHide: t('aurora.passwordHide'),
    // Zonder variabelen: `{label}` blijft staan en Combobox vult hem per chip in.
    comboboxRemove: t('aurora.comboboxRemove'),
  };
}

export function TranslatedAuroraLabels({ children }: { children?: ReactNode }) {
  const labels = useAuroraI18nLabels();
  return <AuroraLabelsProvider labels={labels}>{children}</AuroraLabelsProvider>;
}
